import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { DashboardService } from '@pages/dashboard/dashboard.service';
import { filter, firstValueFrom } from 'rxjs';
import { WathiqStatusEnums } from '@common/enums/wathiq.enum';
import { KYCStatusEnum } from '@common/enums/kyc-status.enum';
import { InvestorProfile } from '@common/interfaces/investor-profile';
import { FirebaseRemoteConfigService } from '@common/services/firebase-remote-config.service';

/**
 * Fetches the investor profile and performs basic validation (existence, type).
 * Redirects if validation fails.
 * @returns The investor profile if valid, or null if redirected.
 */
export const getWathiqProfileOrRedirect = async (
  dashboardService: DashboardService,
  router: Router
) => {
  // Fetch investor profile
  await firstValueFrom(dashboardService.fetchInvestorProfileData());

  await firstValueFrom(
    dashboardService.investorProfile.pipe(
      filter(profile => profile !== undefined)
    )
  );

  const investorProfile = dashboardService.investorProfile.getValue();

  // If investor type is 1 (individual), redirect to nafath instead
  if (investorProfile?.investor?.type === 1) {
    router.navigate(['/auth/nafath']);
    return null;
  }

  // If investor type is 2 (corporate), return the profile
  if (investorProfile?.investor?.type === 2) {
    return investorProfile;
  }

  // Default: redirect to dashboard
  router.navigate(['/dashboard']);
  return null;
};

/**
 * Centralized logic to check access to Wathiq pages based on status.
 *
 * Status Definitions:
 * - NULL/undefined: Entry point, hasn't integrated yet
 * - PENDING: After entering valid CR, can only navigate within flow (success -> preview -> form)
 * - CREATED: Loading state from BE, treat same as PENDING
 * - COMPLETED: Form submitted, cannot access any wathiq screens
 * - FAILED: Service failed, show error on entry point, can retry
 * - DISCARDED: User clicked "data is not correct", can start over
 *
 * STRICT RULE FOR PENDING: No manual URL navigation allowed, only programmatic navigation with state
 */
const checkWathiqAccess = async (
  target: 'wathiq' | 'preview' | 'success' | 'form',
  dashboardService: DashboardService,
  router: Router
): Promise<boolean> => {
  // First, try to use cached profile to avoid race conditions
  let profile: InvestorProfile | null | undefined =
    dashboardService.investorProfile.getValue();

  // If no cached profile, fetch it
  if (!profile) {
    profile = await getWathiqProfileOrRedirect(dashboardService, router);
    if (!profile) return false;
  }

  // Read status from profile (cached or freshly fetched)
  const wathiqStatus = profile.investor?.wathqStatus;
  const kycStatus = profile.investor?.kycSubmissionRequestStatus;

  // Check current URL and navigation
  const currentUrl = router.url || '';
  const isComingFromPreview = currentUrl.includes('/auth/wathiq-preview');
  const nav = router.getCurrentNavigation();

  // Rule 1: COMPLETED status -> Block all Wathiq pages, redirect to KYC or dashboard
  if (wathiqStatus === WathiqStatusEnums.COMPLETED) {
    if (kycStatus !== KYCStatusEnum.APPROVED) {
      router.navigate(['/auth/kyc']);
    } else {
      router.navigate(['/dashboard']);
    }

    return false;
  }

  // Rule 2: PENDING or CREATED status -> STRICT flow enforcement
  if (
    wathiqStatus === WathiqStatusEnums.PENDING ||
    wathiqStatus === WathiqStatusEnums.CREATED
  ) {
    // Always block wathiq entry when PENDING/CREATED
    if (target === 'wathiq') {
      router.navigate(['/auth/wathiq-preview'], { replaceUrl: true });
      return false;
    }

    // For success page: Only allow from wathiq component (after CR verification)
    if (target === 'success') {
      const isComingFromWathiq =
        currentUrl.includes('/auth/wathiq') &&
        !currentUrl.includes('/wathiq-preview') &&
        !currentUrl.includes('/wathiq-success') &&
        !currentUrl.includes('/wathiq-form');

      if (!isComingFromWathiq) {
        router.navigate(['/auth/wathiq-preview'], { replaceUrl: true });
        return false;
      }

      return true;
    }

    // For preview: Always allow (it's the main hub during PENDING/CREATED)
    if (target === 'preview') {
      return true;
    }

    // For form: Only allow from preview with state OR from preview component navigation
    if (target === 'form') {
      const hasAllowedState = nav?.extras?.state?.['allowed'] === true;

      if (!hasAllowedState && !isComingFromPreview) {
        router.navigate(['/auth/wathiq-preview'], { replaceUrl: true });
        return false;
      }

      return true;
    }

    return true;
  }

  // Rule 3: NULL, FAILED, or DISCARDED status -> Allow wathiq entry, block others
  if (target !== 'wathiq') {
    // If coming from preview for success, redirect back to preview
    if (target === 'success' && isComingFromPreview) {
      router.navigate(['/auth/wathiq-preview'], { replaceUrl: true });
      return false;
    }

    router.navigate(['/auth/wathiq']);
    return false;
  }

  return true;
};

// wathiq_rollback - Guard to check if wathiq flow is enabled
export const wathiqGuard: CanActivateFn = async () => {
  const dashboardService = inject(DashboardService);
  const router = inject(Router);
  const firebaseRemoteConfigService = inject(FirebaseRemoteConfigService);

  // wathiq_rollback - Check if wathiq flow is enabled (using centralized signal)
  if (!firebaseRemoteConfigService.turn_on_watheeq_flow()) {
    router.navigate(['/dashboard']);
    return false;
  }

  return checkWathiqAccess('wathiq', dashboardService, router);
};

// wathiq_rollback - Guard to check if wathiq preview is enabled
export const wathiqPreviewGuard: CanActivateFn = async () => {
  const dashboardService = inject(DashboardService);
  const router = inject(Router);
  const firebaseRemoteConfigService = inject(FirebaseRemoteConfigService);

  // wathiq_rollback - Check if wathiq flow is enabled (using centralized signal)
  if (!firebaseRemoteConfigService.turn_on_watheeq_flow()) {
    router.navigate(['/dashboard']);
    return false;
  }

  return checkWathiqAccess('preview', dashboardService, router);
};

// wathiq_rollback - Guard to check if wathiq success is enabled
export const wathiqSuccessGuard: CanActivateFn = async () => {
  const dashboardService = inject(DashboardService);
  const router = inject(Router);
  const firebaseRemoteConfigService = inject(FirebaseRemoteConfigService);

  // wathiq_rollback - Check if wathiq flow is enabled (using centralized signal)
  if (!firebaseRemoteConfigService.turn_on_watheeq_flow()) {
    router.navigate(['/dashboard']);
    return false;
  }

  return checkWathiqAccess('success', dashboardService, router);
};

// wathiq_rollback - Guard to check if wathiq form is enabled
export const wathiqFormGuard: CanActivateFn = async () => {
  const dashboardService = inject(DashboardService);
  const router = inject(Router);
  const firebaseRemoteConfigService = inject(FirebaseRemoteConfigService);

  // wathiq_rollback - Check if wathiq flow is enabled (using centralized signal)
  if (!firebaseRemoteConfigService.turn_on_watheeq_flow()) {
    router.navigate(['/dashboard']);
    return false;
  }

  // checkWathiqAccess will handle all validation including state checking
  return checkWathiqAccess('form', dashboardService, router);
};
